import { Link } from 'react-router';

import { cn } from '@april9au/react-common/utils';

import { SectionHeader } from './section-header';

export type PropertyPathItem = {
  number: string;
  title: string;
  description: string;
  href: string;
  linkLabel: string;
  /** First path is the dark "hero" card in the mockup; the rest sit on white. */
  variant?: 'dark' | 'light';
};

/**
 * "Wherever you are on your property journey" style segmentation grid —
 * lets a visitor self-select into selling/buying/renting/undecided instead
 * of reading a generic services list. Reusable anywhere that split matters
 * (homepage, a future About page).
 */
export function PropertyPaths({
  id,
  eyebrow,
  heading,
  description,
  items,
  className,
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  description?: string;
  items: Array<PropertyPathItem>;
  className?: string;
}) {
  return (
    <section id={id} className={cn('section', className)}>
      <div className="container">
        <SectionHeader eyebrow={eyebrow} heading={heading} description={description} align="left" className="mb-12" />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => {
            const dark = item.variant === 'dark';

            return (
              <Link
                key={item.title}
                to={item.href}
                className={cn(
                  'group flex min-h-[15.5rem] flex-col gap-3.5 rounded-md p-8 pb-7 transition-colors',
                  dark
                    ? 'bg-navy text-foreground'
                    : 'bg-white border border-black/10 text-navy hover:border-black/25',
                )}
              >
                <span className={cn('text-xs font-medium tracking-[0.2em]', dark ? 'text-gold' : 'text-navy/70')}>
                  {item.number}
                </span>
                <h3 className="text-h3">{item.title}</h3>
                <p className={cn('text-sm/relaxed', dark ? 'text-muted-foreground' : 'text-navy/72')}>
                  {item.description}
                </p>
                <span className={cn('link-rule mt-auto', !dark && 'link-rule--on-light')}>{item.linkLabel}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
